import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Checkbox, Tooltip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ListFormLayout from '../../Components/layout/ListFormLayout';
import SearchIcon from '@mui/icons-material/Search';
import CompanyOrBuSelector from '../../Components/CompanyOrBuSelector';
import AddIcon from '@mui/icons-material/Add';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TuneIcon from '@mui/icons-material/Tune';
import { Link } from 'react-router-dom';
import reqProvider from '../../util/requests/reqProvider';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ClearIcon from '@mui/icons-material/Clear';
import RecyclingIcon from '@mui/icons-material/Recycling';
import LocalDrinkIcon from '@mui/icons-material/LocalDrink';
import DoneIcon from '@mui/icons-material/Done';

interface Company {
  id: number;
  name: string;
  posSetSelectorPrivileges: {
    createPrivilege: boolean | null;
    deletePrivilege: boolean | null;
    getPrivilege: boolean;
    getPrivilegeCompany: boolean;
    modifyPrivilege: boolean | null;
  };
}

interface VatGroup {
  id: number;
  name: string;
}

interface Product {
  id: number;
  name: string;
  mainCategoryId: number;
  mainCategoryName: string;
  subCategoryId: number;
  subCategoryName: string;
  customCategory: string | null;
  isTakeawayAllowed: boolean;
  takeawayVatId: number;
  vatId: number;
  tags: { id: number; name: string }[];
  units: {
    hasRecipe: boolean;
    id: number;
    unit: number;
    unitName: string;
    quantity: number;
    defaultPrice: number;
    bulk: boolean;
    depositFeeList: {
      depositFeePrice: number;
      hasDepositFee: string;
      depositFeeCount: number;
    }[];
  }[];
}

const ProductList: React.FC = () => {
  const [companies] = useState<Company[]>([
    {
      "id": 393,
      "name": "BDPST Pub Management Korlátolt Felelősségű Társaság",
      "posSetSelectorPrivileges": {
        "getPrivilege": true,
        "getPrivilegeCompany": true,
        "createPrivilege": null,
        "modifyPrivilege": null,
        "deletePrivilege": null
      }
    },
    {
      "id": 119,
      "name": "Fülöp cége",
      "posSetSelectorPrivileges": {
        "getPrivilege": true,
        "getPrivilegeCompany": true,
        "createPrivilege": null,
        "modifyPrivilege": null,
        "deletePrivilege": null
      }
    }
  ]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [visibleUnits, setVisibleUnits] = useState<number[]>([]);
  const [vatGroup, setVatGroup] = useState<VatGroup[] | null>(null);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    fetchProducts(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchProducts(0, newRowsPerPage);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedIds(products.map(product => product.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (event: React.ChangeEvent<HTMLInputElement>, id: number) => {
    event.stopPropagation();
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      } else {
        return [...prev, id];
      }
    });
  };



  const fetchProducts = async (currentPage, pageSize) => {
    if (!selectedCompany) return;

    setLoading(true);
    try {
      const response = await reqProvider.get('/product', {
        nameSearch: '',
        page: currentPage,
        posSetId: selectedCompany.id,
        size: pageSize,
        sort: 'id,ASC',
      });
      setProducts(response.data.content || []);
      setTotalElements(response.data.totalElements);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(page, rowsPerPage);
  }, [selectedCompany]);

  useEffect(() => {
    reqProvider.get('/product/vatGroup')
      .then((response) => {
        setVatGroup(response.data.content);
      })
      .catch((error) => {
        console.error('Failed to fetch VAT groups:', error);
      });
    },[]);

  return (
    <ListFormLayout title={'Termékek'}>
      <Box sx={{ padding: '16px' }}>
        <CompanyOrBuSelector
          menuItems={companies}
          label={'Válasszon céget, vagy üzleti egységet'}
          value={selectedCompany || null}
          setValue={setSelectedCompany}
          posSet={'productPosSetId'}
        />

        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: '16px',
          marginBottom: '16px',
          justifyContent: 'space-between'
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            flexWrap: { sm: 'wrap' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: '16px'
          }}>
            <TextField
              placeholder={'Termékek keresése...'}
              variant="outlined"
              autoComplete={'off'}
              sx={{
                width: { xs: '100%', sm: '300px' },
                backgroundColor: '#F3E4E9',
                borderRadius: '50px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '50px',
                },
              }}
              InputProps={{
                startAdornment: (
                  <Box sx={{ display: 'flex', alignItems: 'center', paddingX: '8px' }}>
                    <SearchIcon />
                  </Box>
                ),
              }}
            />
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              flexWrap: { sm: 'wrap' },
              gap: '16px',
              width: '100%'
            }}>
              <Button variant="contained" sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}> <TuneIcon /> Oszlopok </Button>
              <Button variant="outlined" sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}> <FileUploadIcon /> Exportálás </Button>
              <Button variant="outlined" sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}> <FileDownloadIcon /> Termékek importálása </Button>
            </Box>
          </Box>
          <Button
            component={Link}
            to="create"
            variant="contained"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              alignSelf: { xs: 'flex-start', md: 'center' }
            }}
          >
            <AddIcon /> Új Termék
          </Button>
        </Box>

        <TableContainer component={Paper} sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '10px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          marginTop: 2
        }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#EDDFE3' }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={products.length > 0 && selectedIds.length === products.length}
                    indeterminate={selectedIds.length > 0 && selectedIds.length < products.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Név</TableCell>
                <TableCell>Gyűjtő kategória</TableCell>
                <TableCell>Főkategória</TableCell>
                <TableCell>Alkategória</TableCell>
                <TableCell>ÁFA csoport</TableCell>
                <TableCell>Elvihető</TableCell>
                <TableCell>Elviteles ÁFA</TableCell>
                <TableCell>Termék Kiszerelései</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((product) => (
                <React.Fragment key={product.id}>
                <TableRow
                  key={product.id}
                  hover
                  selected={selectedIds.includes(product.id)}
                  component={Link}
                  to={`/products/update/${product.id}`}
                  sx={{textDecoration: 'none',
                    color: 'inherit'
                  }}
                  onClick={(e) => {
                    const target = e.target as HTMLElement;
                    if (target.closest('.MuiCheckbox-root') || target.closest('button')) {
                      e.preventDefault();
                    }
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.includes(product.id)}
                      onChange={(event) => handleSelectOne(event, product.id)}
                    />
                  </TableCell>
                  <TableCell>{product.id}</TableCell>
                  <TableCell>{product.name}</TableCell>
                  <TableCell>{product.customCategory?.split('/.')[0]}</TableCell>
                  <TableCell>{product.customCategory?.split('/.')[1]}</TableCell>
                  <TableCell>{product.customCategory?.split('/.')[2]}</TableCell>
                  <TableCell>{vatGroup?.find((vat) => {
                    return vat.id === product.vatId;
                  })?.name}</TableCell>
                  <TableCell>{product.isTakeawayAllowed ? 'Igen' : 'Nem'}</TableCell>
                  <TableCell>{vatGroup?.find((vat) => {
                    return vat.id === product.takeawayVatId;
                  })?.name}</TableCell>
                  <TableCell>
                    <Button
                      sx={{textTransform: 'none'}}
                      onClick={() => {
                        setVisibleUnits(prev =>
                          prev.includes(product.id)
                            ? prev.filter(id => id !== product.id)
                            : [...prev, product.id],
                        );
                      }}
                      startIcon={visibleUnits.includes(product.id) ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    >
                      {visibleUnits.includes(product.id) ? 'Kiszerelések elrejtése' : 'Kiszerelések megtekintése'}
                    </Button>

                  </TableCell>
                </TableRow>
                  {visibleUnits.includes(product.id) && (
                    <TableRow sx={{backgroundColor: '#F9EAEF'}}>
                      <TableCell colSpan={12} sx={{ p: 0 }}>
                        <Box
                          sx={{
                            backgroundColor: '#FFFFFF99',
                            borderRadius: 1,
                            m: 2,  // margin minden oldalon
                            boxShadow: 1,  // enyhe árnyék
                            position: 'relative',
                            overflow: 'hidden'  // ha esetleg valami túllógna
                          }}
                        >
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{backgroundColor: '#EDDFE3'}}>
                                <TableCell>Kiszerelés</TableCell>
                                <TableCell>Alapértelmezett bruttó ár</TableCell>
                                <TableCell>Kimért termék</TableCell>
                                <TableCell>Receptúra</TableCell>
                                <TableCell>Visszaváltható palack</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {product.units.map((unit) => (
                                <TableRow key={unit.id}>
                                  <TableCell>{`${unit.quantity} ${unit.unitName}`}</TableCell>
                                  <TableCell>{`${unit.defaultPrice} Ft`}</TableCell>
                                  <TableCell>{unit.bulk ? <DoneIcon /> : <ClearIcon/> }</TableCell>
                                  <TableCell>{unit.hasRecipe ? (<Tooltip title={"Receptúra"}><ReceiptLongIcon /></Tooltip>) : <ClearIcon />}</TableCell>
                                  <TableCell>{
                                    (<>
                                      {unit.depositFeeList.some(deposit => ['ONE_WAY', 'MULTI_WAY'].includes(deposit.hasDepositFee)) && (
                                        <Tooltip title={'Visszaváltható palack'}>
                                          <RecyclingIcon sx={{ mr: 1 }} />
                                        </Tooltip>
                                      )}
                                      {unit.depositFeeList.some(deposit => ['REVO_CUP'].includes(deposit.hasDepositFee)) && (
                                        <Tooltip title={'Visszaváltható pohár'}>
                                          <LocalDrinkIcon />
                                        </Tooltip>
                                      )}
                                      {!unit.depositFeeList.some(deposit =>
                                        ['ONE_WAY', 'MULTI_WAY', 'REVO_CUP'].includes(deposit.hasDepositFee),
                                      ) && <ClearIcon />}
                                    </>)
                                  }</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalElements}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[50, 100, 200, 500]}
            labelRowsPerPage="Sorok száma:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} / ${count}`
            }
          />

        </TableContainer>
      </Box>
    </ListFormLayout>
  );
};

export default ProductList;
